import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Select,
  MenuItem,
  IconButton,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Chip,
  useTheme,
  Menu,
  TablePagination,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Snackbar,
  FormControl,
  InputLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import FilterListIcon from '@mui/icons-material/FilterList';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import RestoreFromTrashIcon from '@mui/icons-material/RestoreFromTrash';
import { BaseLabel, labelsService, LabelType, LabelCategory } from '../../services/labels-service';
import AddLabelPanel from './AddLabelPanel';
import EditLabelPanel from './EditLabelPanel';
import { formatFriendlyDate } from '../../utils/dateUtils';

const LABEL_TYPES: LabelType[] = ['BUTTON', 'TITLE', 'MESSAGE', 'ERROR', 'ANY', 'FOOTNOTE', 'TOOLTIP'];
const LABEL_CATEGORIES: LabelCategory[] = ['COLLECTION_POINT', 'EMAIL', 'GENERAL'];

const Labels: React.FC = () => {
  const theme = useTheme();
  const [sortBy, setSortBy] = useState('A-Z');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedItems, setSelectedItems] = useState<number[]>([]);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [labels, setLabels] = useState<BaseLabel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddPanelOpen, setIsAddPanelOpen] = useState(false);
  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false);
  const [selectedLabel, setSelectedLabel] = useState<BaseLabel | null>(null);
  const [menuRowId, setMenuRowId] = useState<number | null>(null);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [filters, setFilters] = useState({
    category: 'ALL',
    labelType: 'ALL',
    isPublished: 'ALL'
  });
  const [totalCount, setTotalCount] = useState(0);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    title: '',
    message: '',
    onConfirm: () => {},
  });

  const fetchLabels = async () => {
    try {
      setLoading(true);
      setError(null);
      const result = await labelsService.getBaseLabels({
        category: filters.category === 'ALL' ? undefined : filters.category as LabelCategory,
        labelType: filters.labelType === 'ALL' ? undefined : filters.labelType as LabelType,
        isPublished: filters.isPublished === 'ALL' ? undefined : filters.isPublished === 'PUBLISHED'
      }, {
        page: page + 1, // Convert to 1-based index for server
        pageSize: rowsPerPage
      });
      if (result && result.labels) {
        setLabels(result.labels);
        setTotalCount(result.total);
      } else {
        setLabels([]);
        setTotalCount(0);
      }
    } catch (error) {
      console.error('Error fetching labels:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch labels');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLabels();
  }, [page, rowsPerPage]); // Removed filters from dependencies

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page when changing rows per page
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedItems(labels.map(label => label.LabelId));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelect = (id: number) => {
    setSelectedItems(prev =>
      prev.includes(id)
        ? prev.filter(item => item !== id)
        : [...prev, id]
    );
  };

  const handleDelete = async (id: number) => {
    try {
      await labelsService.deleteBaseLabel(id);
      setLabels(prev => prev.filter(label => label.LabelId !== id));
    } catch (error) {
      console.error('Error deleting label:', error);
    }
  };

  const handleStatusChange = async (id: number, newStatus: boolean) => {
    try {
      await labelsService.updateBaseLabel(id, { IsActive: newStatus });
      setLabels(prev => prev.map(label => 
        label.LabelId === id ? { ...label, IsActive: newStatus } : label
      ));
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const getStatusColor = (status: boolean): 'success' | 'default' => {
    return status ? 'success' : 'default';
  };

  const getStatusLabel = (status: boolean) => {
    return status ? 'Active' : 'Inactive';
  };

  const handleEdit = () => {
    if (selectedItems.length === 1) {
      const label = labels.find(l => l.LabelId === selectedItems[0]) || null;
      setSelectedLabel(label);
      setIsEditPanelOpen(true);
    }
  };

  const handleConfirmDialog = (title: string, message: string, onConfirm: () => void) => {
    setConfirmDialog({
      open: true,
      title,
      message,
      onConfirm,
    });
  };

  const handleCloseConfirmDialog = () => {
    setConfirmDialog(prev => ({ ...prev, open: false }));
  };

  const handleConfirmAction = () => {
    confirmDialog.onConfirm();
    handleCloseConfirmDialog();
  };

  const handleFilterChange = (field: keyof typeof filters, value: string) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleApplyFilters = async () => {
    try {
      setLoading(true);
      const result = await labelsService.getBaseLabels({
        category: filters.category === 'ALL' ? undefined : filters.category as LabelCategory,
        labelType: filters.labelType === 'ALL' ? undefined : filters.labelType as LabelType,
        isPublished: filters.isPublished === 'ALL' ? undefined : filters.isPublished === 'PUBLISHED'
      }, {
        page: page + 1,
        pageSize: rowsPerPage
      });
      if (result && result.labels) {
        setLabels(result.labels);
        setTotalCount(result.total);
      } else {
        setLabels([]);
        setTotalCount(0);
      }
      setIsFilterModalOpen(false);
    } catch (error) {
      console.error('Error applying filters:', error);
      setError(error instanceof Error ? error.message : 'Failed to apply filters');
    } finally {
      setLoading(false);
    }
  };

  // Add a function to check if any filters are applied
  const hasActiveFilters = () => {
    return filters.category !== 'ALL' || 
           filters.labelType !== 'ALL' || 
           filters.isPublished !== 'ALL';
  };

  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        height: '100%',
        color: theme.palette.text.primary
      }}>
        Loading labels...
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, bgcolor: theme.palette.background.default }}>
      <Typography variant="h4" sx={{ 
        mb: theme.custom.titleSpacing.marginBottom, 
        color: theme.palette.text.primary 
      }}>
        Labels
      </Typography>
      
      {error && (
        <Snackbar 
          open={!!error} 
          autoHideDuration={6000} 
          onClose={() => setError(null)}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <Alert onClose={() => setError(null)} severity="error" sx={{ width: '100%' }}>
            {error}
          </Alert>
        </Snackbar>
      )}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          {/* Removed selected items actions */}
        </Box>

        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
          <Select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            size="small"
            sx={{ 
              width: 150,
              bgcolor: theme.palette.background.paper,
              color: theme.palette.text.primary,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.divider,
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.primary.main,
              },
              '& .MuiSelect-icon': {
                color: theme.palette.text.secondary,
              },
            }}
          >
            <MenuItem value="A-Z" sx={{ color: theme.palette.text.primary }}>A-Z</MenuItem>
            <MenuItem value="Z-A" sx={{ color: theme.palette.text.primary }}>Z-A</MenuItem>
            <MenuItem value="newest" sx={{ color: theme.palette.text.primary }}>Newest</MenuItem>
            <MenuItem value="oldest" sx={{ color: theme.palette.text.primary }}>Oldest</MenuItem>
          </Select>
          <IconButton 
            onClick={() => setIsFilterModalOpen(true)}
            sx={{ 
              color: theme.palette.text.primary,
              '&:hover': {
                bgcolor: theme.palette.action.hover,
              },
              position: 'relative',
              '&::after': hasActiveFilters() ? {
                content: '""',
                position: 'absolute',
                top: 0,
                right: 0,
                width: 8,
                height: 8,
                borderRadius: '50%',
                bgcolor: theme.palette.primary.main,
                border: `2px solid ${theme.palette.background.paper}`,
              } : {}
            }}
          >
            <FilterListIcon />
          </IconButton>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setIsAddPanelOpen(true)}
            sx={{ 
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              '&:hover': { 
                bgcolor: theme.palette.primary.dark 
              },
              '& .MuiSvgIcon-root': {
                color: theme.palette.primary.contrastText,
              }
            }}
          >
            ADD NEW
          </Button>
        </Box>
      </Box>

      <TableContainer 
        component={Paper}
        sx={{
          bgcolor: theme.palette.background.paper,
          '& .MuiTable-root': {
            borderCollapse: 'separate',
            borderSpacing: 0,
          }
        }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ color: theme.palette.text.primary }}>Label Key</TableCell>
              <TableCell sx={{ color: theme.palette.text.primary }}>Text</TableCell>
              <TableCell sx={{ color: theme.palette.text.primary }}>Type</TableCell>
              <TableCell sx={{ color: theme.palette.text.primary }}>Category</TableCell>
              <TableCell sx={{ color: theme.palette.text.primary }}>Created on</TableCell>
              <TableCell sx={{ color: theme.palette.text.primary }}>Status</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {labels.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} sx={{ borderBottom: 'none' }}>
                  <Box sx={{ 
                    display: 'flex', 
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    py: 8
                  }}>
                    <Typography 
                      variant="h6" 
                      sx={{ 
                        mb: 3,
                        color: theme.palette.text.secondary,
                        textAlign: 'center'
                      }}
                    >
                      There are no labels to display. Click the button below to create your first label
                    </Typography>
                    <Button
                      variant="contained"
                      startIcon={<AddIcon />}
                      onClick={() => setIsAddPanelOpen(true)}
                      sx={{ 
                        bgcolor: theme.palette.primary.main,
                        color: theme.palette.primary.contrastText,
                        '&:hover': { 
                          bgcolor: theme.palette.primary.dark 
                        },
                        '& .MuiSvgIcon-root': {
                          color: theme.palette.primary.contrastText,
                        }
                      }}
                    >
                      ADD NEW
                    </Button>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              labels.map((label) => (
                <TableRow
                  key={label.LabelId}
                  hover
                  sx={{
                    '&.MuiTableRow-root:hover': {
                      bgcolor: theme.palette.action.hover,
                    }
                  }}
                >
                  <TableCell 
                    sx={{ 
                      color: theme.palette.text.primary,
                      cursor: 'pointer',
                      '&:hover': {
                        color: theme.palette.primary.main,
                        textDecoration: 'underline'
                      }
                    }}
                    onClick={() => {
                      setSelectedLabel(label);
                      setIsEditPanelOpen(true);
                    }}
                  >
                    {label.LabelKey}
                  </TableCell>
                  <TableCell sx={{ color: theme.palette.text.primary }}>{label.LabelText}</TableCell>
                  <TableCell sx={{ color: theme.palette.text.primary }}>{label.LabelType}</TableCell>
                  <TableCell sx={{ color: theme.palette.text.primary }}>{label.Category}</TableCell>
                  <TableCell sx={{ color: theme.palette.text.primary }}>{formatFriendlyDate(label.CreatedDate)}</TableCell>
                  <TableCell>
                    <Chip 
                      label={label.IsPublished ? 'Published' : 'Draft'}
                      size="small"
                      color={label.IsPublished ? 'success' : 'default'}
                      sx={{
                        '& .MuiChip-label': {
                          color: theme.palette.getContrastText(
                            label.IsPublished 
                              ? theme.palette.success.main 
                              : theme.palette.grey[500]
                          ),
                        },
                      }}
                    />
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            color: theme.palette.text.primary,
            bgcolor: theme.palette.background.paper,
            borderTop: `1px solid ${theme.palette.divider}`,
            '& .MuiTablePagination-select': {
              color: theme.palette.text.primary,
            },
            '& .MuiTablePagination-selectIcon': {
              color: theme.palette.text.primary,
            },
            '& .MuiTablePagination-displayedRows': {
              color: theme.palette.text.primary,
            },
            '& .MuiIconButton-root': {
              color: theme.palette.text.primary,
              '&.Mui-disabled': {
                color: theme.palette.action.disabled,
              },
            },
          }}
        />
      </TableContainer>

      <AddLabelPanel 
        open={isAddPanelOpen}
        onClose={() => setIsAddPanelOpen(false)}
        onSave={fetchLabels}
      />

      <EditLabelPanel
        open={isEditPanelOpen}
        onClose={() => setIsEditPanelOpen(false)}
        label={selectedLabel}
        onSave={fetchLabels}
      />

      <Dialog
        open={confirmDialog.open}
        onClose={handleCloseConfirmDialog}
        sx={{
          '& .MuiDialog-paper': {
            bgcolor: theme.palette.background.paper,
            color: theme.palette.text.primary,
          },
        }}
      >
        <DialogTitle>{confirmDialog.title}</DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseConfirmDialog} sx={{ color: theme.palette.text.primary }}>
            Cancel
          </Button>
          <Button onClick={handleConfirmAction} variant="contained" color="primary">
            Confirm
          </Button>
        </DialogActions>
      </Dialog>

      {/* Filter Modal */}
      <Dialog
        open={isFilterModalOpen}
        onClose={() => setIsFilterModalOpen(false)}
        maxWidth="sm"
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            bgcolor: theme.palette.background.paper,
            color: theme.palette.text.primary,
          },
        }}
      >
        <DialogTitle>Filters</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}>
            <FormControl fullWidth>
              <InputLabel sx={{ color: theme.palette.text.secondary }}>Category</InputLabel>
              <Select
                value={filters.category}
                onChange={(e) => handleFilterChange('category', e.target.value)}
                label="Category"
                sx={{
                  color: theme.palette.text.primary,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.divider,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                <MenuItem value="ALL" sx={{ color: theme.palette.text.primary }}>All Categories</MenuItem>
                {LABEL_CATEGORIES.map((category) => (
                  <MenuItem key={category} value={category} sx={{ color: theme.palette.text.primary }}>
                    {category}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel sx={{ color: theme.palette.text.secondary }}>Type</InputLabel>
              <Select
                value={filters.labelType}
                onChange={(e) => handleFilterChange('labelType', e.target.value)}
                label="Type"
                sx={{
                  color: theme.palette.text.primary,
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.divider,
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                <MenuItem value="ALL" sx={{ color: theme.palette.text.primary }}>All Types</MenuItem>
                {LABEL_TYPES.map((type) => (
                  <MenuItem key={type} value={type} sx={{ color: theme.palette.text.primary }}>
                    {type}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl>
              <RadioGroup
                value={filters.isPublished}
                onChange={(e) => handleFilterChange('isPublished', e.target.value)}
              >
                <FormControlLabel 
                  value="PUBLISHED" 
                  control={<Radio />} 
                  label="Published"
                  sx={{ color: theme.palette.text.primary }}
                />
                <FormControlLabel 
                  value="DRAFT" 
                  control={<Radio />} 
                  label="Draft"
                  sx={{ color: theme.palette.text.primary }}
                />
                <FormControlLabel 
                  value="ALL" 
                  control={<Radio />} 
                  label="Both"
                  sx={{ color: theme.palette.text.primary }}
                />
              </RadioGroup>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setIsFilterModalOpen(false)}
            sx={{ color: theme.palette.text.primary }}
          >
            Cancel
          </Button>
          <Button 
            variant="contained" 
            onClick={handleApplyFilters}
            sx={{
              bgcolor: theme.palette.primary.main,
              color: theme.palette.primary.contrastText,
              '&:hover': {
                bgcolor: theme.palette.primary.dark,
              },
            }}
          >
            Apply
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Labels; 